import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Video, HelpCircle, PlusCircle, Trash2, FileText, Upload } from "lucide-react";
import { api } from "@/lib/api";

function fmtBytes(n) {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function LessonAttachments({ lesson, onChanged }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const attachments = lesson.attachments || [];

    async function handleUpload(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true); setErr(null);
        try {
            await api.adminUploadLessonAttachment(lesson.id, file);
            await onChanged?.();
        } catch (ex) { setErr(ex.message || "Upload failed."); }
        finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    }

    async function handleDelete(attId, filename) {
        if (!window.confirm(`Delete attachment "${filename}"? This removes it from disk.`)) return;
        setBusy(true); setErr(null);
        try {
            await api.adminDeleteLessonAttachment(lesson.id, attId);
            await onChanged?.();
        } catch (ex) { setErr(ex.message || "Delete failed."); }
        finally { setBusy(false); }
    }

    return (
        <div className="pt-3 mt-2 space-y-2" style={{ borderTop: "1px dashed var(--st-line)" }} data-testid={`lesson-attachments-${lesson.id}`}>
            <div className="flex items-center justify-between">
                <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-royal)" }}>
                    <FileText size={12} className="inline mr-1" /> PDF attachments ({attachments.length})
                </div>
                <div>
                    <input ref={inputRef} type="file" accept="application/pdf,.pdf" onChange={handleUpload}
                           disabled={busy} className="hidden"
                           data-testid={`attachment-input-${lesson.id}`} />
                    <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
                            className="btn-outline-navy text-xs inline-flex items-center gap-1"
                            data-testid={`attachment-upload-btn-${lesson.id}`}>
                        <Upload size={12} /> {busy ? "Uploading…" : "Upload PDF"}
                    </button>
                </div>
            </div>
            {err && <div className="text-xs" style={{ color: "#B42318" }} data-testid={`attachment-err-${lesson.id}`}>{err}</div>}
            <ul className="space-y-1.5 text-xs">
                {attachments.map((a) => (
                    <li key={a.id} className="flex items-center justify-between p-2 rounded"
                        style={{ background: "var(--st-white)", border: "1px solid var(--st-line)" }}
                        data-testid={`attachment-row-${a.id}`}>
                        <div className="min-w-0 flex-1">
                            <div className="truncate font-mono" style={{ color: "var(--st-ink)" }} title={a.filename}>
                                {a.filename}
                            </div>
                            <div style={{ color: "var(--st-silver-500)" }}>
                                {fmtBytes(a.size_bytes)} · uploaded {a.uploaded_at ? new Date(a.uploaded_at).toLocaleDateString() : "—"}
                            </div>
                        </div>
                        <button type="button" onClick={() => handleDelete(a.id, a.filename)}
                                className="btn-ghost text-xs inline-flex items-center gap-1 flex-shrink-0"
                                data-testid={`attachment-delete-${a.id}`}>
                            <Trash2 size={12} /> Delete
                        </button>
                    </li>
                ))}
                {attachments.length === 0 && (
                    <li className="text-xs italic" style={{ color: "var(--st-silver-500)" }}>
                        No PDFs uploaded yet.
                    </li>
                )}
            </ul>
        </div>
    );
}

function LessonEditor({ lesson, onSaved, onDelete }) {
    const [form, setForm] = useState({
        title: lesson.title || "",
        description: lesson.description || "",
        video_url: lesson.video_url || "",
        duration_seconds: lesson.duration_seconds || 0,
        reading_content: lesson.reading_content || "",
        order: lesson.order,
    });
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);

    async function save() {
        setBusy(true); setErr(null); setMsg(null);
        try {
            await api.adminPatchLesson(lesson.id, {
                ...form,
                duration_seconds: Number(form.duration_seconds) || 0,
                order: Number(form.order) || lesson.order,
            });
            setMsg("Saved.");
            onSaved?.();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }
    return (
        <div className="p-4 rounded-md space-y-3" style={{ background: "var(--st-silver-50)", border: "1px solid var(--st-line)" }} data-testid={`lesson-editor-${lesson.id}`}>
            <div className="flex items-center gap-2">
                <Video size={14} color="var(--st-royal)" />
                <input className="st-input flex-1" value={form.title}
                       onChange={(e) => setForm({ ...form, title: e.target.value })}
                       data-testid={`lesson-title-${lesson.id}`} />
                <input type="number" className="st-input w-20" value={form.order}
                       onChange={(e) => setForm({ ...form, order: e.target.value })}
                       title="Order" />
            </div>
            <input className="st-input" value={form.video_url}
                   onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                   placeholder="Video URL (YouTube/Vimeo embed)"
                   data-testid={`lesson-video-${lesson.id}`} />
            <div className="grid grid-cols-2 gap-3">
                <input type="number" className="st-input" value={form.duration_seconds}
                       onChange={(e) => setForm({ ...form, duration_seconds: e.target.value })}
                       placeholder="Duration (seconds)" />
                <input className="st-input" value={form.description}
                       onChange={(e) => setForm({ ...form, description: e.target.value })}
                       placeholder="Short description" />
            </div>
            <textarea rows={4} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                      value={form.reading_content}
                      onChange={(e) => setForm({ ...form, reading_content: e.target.value })}
                      placeholder="Reading content (markdown)"
                      data-testid={`lesson-reading-${lesson.id}`} />
            <div className="flex items-center justify-between text-xs">
                <div>
                    {msg && <span style={{ color: "#067647" }}>{msg}</span>}
                    {err && <span style={{ color: "#B42318" }}>{err}</span>}
                </div>
                <div className="flex gap-2">
                    <button type="button" className="btn-ghost text-xs inline-flex items-center gap-1"
                            onClick={() => { if (window.confirm(`Delete lesson "${lesson.title}"? This deletes all student progress on it.`)) onDelete(lesson.id); }}
                            data-testid={`lesson-delete-${lesson.id}`}>
                        <Trash2 size={12} /> Delete
                    </button>
                    <button type="button" className="btn-royal text-xs" onClick={save} disabled={busy} data-testid={`lesson-save-${lesson.id}`}>
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
            <LessonAttachments lesson={lesson} onChanged={onSaved} />
        </div>
    );
}

function ModuleBlock({ module, onSaved, onDelete, onAddLesson, onDeleteLesson }) {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState(module.title);
    const [order, setOrder] = useState(module.order);
    const [description, setDescription] = useState(module.description || "");
    const [saving, setSaving] = useState(false);

    async function saveMod() {
        setSaving(true);
        try {
            await api.adminPatchModule(module.id, { title, description, order: Number(order) || module.order });
            onSaved?.();
        } finally { setSaving(false); }
    }

    return (
        <div className="rounded-md" style={{ border: "1px solid var(--st-line)" }} data-testid={`module-block-${module.id}`}>
            <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 text-left"
                onClick={() => setOpen(!open)}
                data-testid={`module-toggle-${module.id}`}
            >
                <div className="flex items-center gap-2">
                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span className="font-semibold" style={{ color: "var(--st-navy)" }}>{module.title}</span>
                    <span className="text-xs" style={{ color: "var(--st-silver-500)" }}>{module.lessons.length} lessons</span>
                </div>
            </button>
            {open && (
                <div className="px-4 pb-4 space-y-4">
                    <div className="grid grid-cols-[1fr_100px] gap-2 items-end">
                        <div>
                            <label className="st-label">Module title</label>
                            <input className="st-input" value={title} onChange={(e) => setTitle(e.target.value)}
                                   data-testid={`module-title-${module.id}`} />
                        </div>
                        <div>
                            <label className="st-label">Order</label>
                            <input type="number" className="st-input" value={order} onChange={(e) => setOrder(e.target.value)} />
                        </div>
                    </div>
                    <textarea rows={2} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                              value={description} onChange={(e) => setDescription(e.target.value)}
                              placeholder="Short description" />
                    <div className="flex justify-between">
                        <button type="button" onClick={() => onDelete(module.id)} className="btn-ghost text-xs inline-flex items-center gap-1"
                                data-testid={`module-delete-${module.id}`}>
                            <Trash2 size={12} /> Delete module
                        </button>
                        <button type="button" className="btn-royal text-xs" onClick={saveMod} disabled={saving}
                                data-testid={`module-save-${module.id}`}>
                            {saving ? "Saving…" : "Save module"}
                        </button>
                    </div>

                    <div className="space-y-3 mt-2">
                        {module.lessons.map((l) => (
                            <LessonEditor
                                key={l.id}
                                lesson={l}
                                onSaved={onSaved}
                                onDelete={(id) => onDeleteLesson(id)}
                            />
                        ))}
                        <button type="button"
                                onClick={() => onAddLesson(module.id, module.lessons.length + 1)}
                                className="btn-outline-navy text-xs inline-flex items-center gap-1"
                                data-testid={`module-add-lesson-${module.id}`}>
                            <PlusCircle size={12} /> Add lesson
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AdminCurriculum() {
    const [tree, setTree] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    async function load() {
        setErr(null);
        try {
            const res = await api.adminGetCurriculum();
            setTree(res.phases);
        } catch (e) { setErr(e.message); }
    }
    useEffect(() => { load(); }, []);

    async function addModule(phaseId, order) {
        setBusy(true);
        try {
            await api.adminCreateModule({ phase_id: phaseId, order, title: `New module ${order}`, description: "" });
            await load();
        } finally { setBusy(false); }
    }
    async function deleteModule(id) {
        if (!window.confirm("Delete this module?")) return;
        setBusy(true);
        try { await api.adminDeleteModule(id); await load(); }
        finally { setBusy(false); }
    }
    async function addLesson(moduleId, order) {
        setBusy(true);
        try {
            await api.adminCreateLesson({
                module_id: moduleId, order,
                title: `New lesson ${order}`,
                video_url: "https://www.youtube.com/embed/dQw4w9WgXcQ",
                reading_content: "Draft reading content.",
                duration_seconds: 240,
            });
            await load();
        } finally { setBusy(false); }
    }
    async function deleteLesson(id) {
        setBusy(true);
        try { await api.adminDeleteLesson(id); await load(); }
        finally { setBusy(false); }
    }

    return (
        <section className="max-w-5xl mx-auto px-6 py-10 space-y-6" data-testid="admin-curriculum-page">
            <div>
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Curriculum</div>
                <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Phases, modules & lessons</h1>
                <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                    Edit content and structure. Video URLs, reading markdown, and order are all live-editable.
                </p>
            </div>

            {err && <div className="text-sm p-3 rounded" style={{ background: "#FEF3F2", color: "#B42318", border: "1px solid #FECDCA" }}>{err}</div>}
            {!tree && <div className="text-sm">Loading…</div>}

            {tree && tree.map((phase) => (
                <div key={phase.id} className="st-surface p-6 space-y-4" data-testid={`phase-section-${phase.slug}`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>
                                Phase {phase.order}
                            </div>
                            <h2 className="font-serif text-2xl mt-1" style={{ color: "var(--st-navy)" }}>{phase.name}</h2>
                            <div className="text-xs mt-1" style={{ color: "var(--st-ink-muted)" }}>{phase.description}</div>
                        </div>
                        {phase.quiz && (
                            <div className="text-right text-xs" style={{ color: "var(--st-silver-500)" }} data-testid={`phase-quiz-${phase.slug}`}>
                                <HelpCircle size={12} className="inline" /> {phase.quiz.title}
                                <div>{phase.quiz.questions.length} questions · {Math.round(phase.quiz.pass_threshold * 100)}% to pass</div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-3">
                        {phase.modules.map((m) => (
                            <ModuleBlock
                                key={m.id}
                                module={m}
                                onSaved={load}
                                onDelete={deleteModule}
                                onAddLesson={addLesson}
                                onDeleteLesson={deleteLesson}
                            />
                        ))}
                        <button
                            type="button"
                            className="btn-outline-navy text-sm inline-flex items-center gap-2"
                            onClick={() => addModule(phase.id, phase.modules.length + 1)}
                            disabled={busy}
                            data-testid={`add-module-${phase.slug}`}
                        >
                            <PlusCircle size={12} /> Add module to {phase.name}
                        </button>
                    </div>
                </div>
            ))}
        </section>
    );
}
